import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DataTypeEnum } from '../../../shared/ontology/data-type.enum';
import { DropdownPosition, NgSelectComponent } from '@ng-select/ng-select';
import { ObservationVariable, ValueReference } from '../../../shared/model/observation-variable.model';
import { BreedingMethodFilterTypeEnum, BreedingMethodsSelectComponent } from '../../../shared/breeding-methods-select/breeding-methods-select.component';
import { LocationsSelectComponent } from '../../../shared/locations-select/locations-select.component';
import { BreedingMethod } from '../../../shared/breeding-method/model/breeding-method';
import { Location } from '../../../shared/location/model/location';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { CustomStringDateAdapter } from '../adapters/custom-string-date-adapter';
import { CustomDateParserFormatter } from '../adapters/custom-date-parser-formatter';

interface DropdownValue {
    id: string;
    name: string;
    description: string;
}

export interface CellData<T> {
    model?: any;
    isEditing?: boolean;
    options?: string[];
    previousValue?: any;
    row?: number;
    col?: number;
    hasError?: boolean
}

export interface EditingDataMap<T> {
    [key: string]: {
        [key: string]: CellData<T>;
    };
}


@Component({
    selector: 'jhi-study-inline-editor',
    templateUrl: './study-inline-editor.component.html',
    styleUrls: ['./study-inline-editor.component.scss'],
    providers: [
        { provide: NgbDateAdapter, useClass: CustomStringDateAdapter },
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
    ]
})
export class StudyInlineEditorComponent<T extends object> implements OnInit, AfterViewInit {

    LOCATION_ID = 8190;
    BREEDING_METHOD_ID = 8257;
    BREEDING_METHOD_CODE = 8251;
    BREEDING_METHOD_VTE = 8252;


    readonly BreedingMethodFilterTypeEnum = BreedingMethodFilterTypeEnum;
    readonly DataTypeIdEnum = DataTypeEnum;
    categoricalItems: DropdownValue[] = [];
    isCalendarButtonClicked = false;

    @Input() observationVariable: ObservationVariable;
    @Input() cellData: CellData<T>;
    @Input() categoricalBindBy: 'id' | 'name' | 'description' = 'id';
    @Input() bindProperty: 'value' | 'draftValue' = 'value';
    @Input() isCategoricalDescriptionView: boolean = false;
    @Input() enableCustomCategoricalItem: boolean = false;
    @Input() breedingMethodFilterType: BreedingMethodFilterTypeEnum = BreedingMethodFilterTypeEnum.ALL_METHODS;
    @Input() categoricalDropdownPosition: DropdownPosition = 'auto';
    @Input() locationSelectClearable: boolean = true;
    @ViewChild('inputElement') inputElement: ElementRef;
    @ViewChild('inputElement', { read: NgSelectComponent }) ngSelect: NgSelectComponent;
    @ViewChild(BreedingMethodsSelectComponent) breedingMethodsSelect: BreedingMethodsSelectComponent;
    @ViewChild(LocationsSelectComponent) locationSelect: LocationsSelectComponent;

    @Output() onBlur = new EventEmitter<void>();
    @Output() onKeyDown = new EventEmitter<KeyboardEvent>();
    @Output() selectedBreedingMethod = new EventEmitter<BreedingMethod>();
    @Output() selectedLocation = new EventEmitter<Location>();

    constructor(protected elementRef: ElementRef) {
    }

    ngOnInit(): void {
        this.categoricalItems = this.convertToOptionDataItems(this.observationVariable.possibleValues);
    }

    ngAfterViewInit() {
        setTimeout(() => {
            if (this.observationVariable.dataTypeCode === 'C') {
                this.ngSelect?.focus();
                if (this.breedingMethodsSelect) {
                    this.breedingMethodsSelect.focus();
                }
                if (this.locationSelect) {
                    this.locationSelect.focus();
                }
                if (!this.isBreedingMethod() && !this.observationVariable.possibleValues || this.observationVariable.possibleValues.length === 0) {
                    this.inputElement?.nativeElement.focus();
                }
            } else {
                this.inputElement?.nativeElement.focus();
            }
        });
    }

    convertToOptionDataItems(possibleValues: ValueReference[]): DropdownValue[] {
        if (possibleValues) {
            return possibleValues.map(possibleValue => ({
                id: possibleValue.id.toString(),
                name: possibleValue.name,
                description: possibleValue.description
            }));
        }
        return [];
    }

    isBreedingMethod() {
        return this.observationVariable.termId === this.BREEDING_METHOD_ID || this.observationVariable.termId === this.BREEDING_METHOD_CODE || this.observationVariable.termId === this.BREEDING_METHOD_VTE;
    }

    isLocation() {
        return this.observationVariable.termId === this.LOCATION_ID;
    }

    handleBlur() {

        // Special case for Calendar control,
        // Only emit blur when the calendar button is not clicked.
        if (this.isCalendarButtonClicked) {
            this.isCalendarButtonClicked = false;
            return;
        }

        this.onBlur.emit();
    }

    handleKeyDown($event: KeyboardEvent) {
        this.onKeyDown.emit($event);
    }

    // If the rendered component is a breeding method select, manually set the
    // studySettingVariable.value with the breeding method code.
    onMethodChanged(breedingMethod: BreedingMethod) {
        this.selectedBreedingMethod.emit(breedingMethod);
        this.handleBlur();
    }

    onLocationChanged($event: Location) {
        this.selectedLocation.emit($event);
    }

    onChange(event: any) {
        // If id is null or undefined it means the value is a custom user input
        if (!event.id) {
            const custom = event.label;
            const existing = this.categoricalItems.find(item => item[this.categoricalBindBy] === custom);
            if (!existing) {
                this.cellData.model[this.bindProperty] = custom;
                this.handleBlur();
            }
        }
    }

    categoricalSearchFn(isCategoricalDescriptionView: boolean, term: string, item: any) {
        term = term.toLocaleLowerCase();
        if (isCategoricalDescriptionView) {
            return item.name && item.name.toLocaleLowerCase().indexOf(term) === 0 || item.description && item.description.toLocaleLowerCase().indexOf(term) === 0;
        } else {
            return item.name && item.name.toLocaleLowerCase().indexOf(term) === 0;
        }
    }

    onCalendarMouseDown(): void {
        this.isCalendarButtonClicked = true;
    }
}
